import * as THREE from 'three';
import type { FaunaCatalog } from './FaunaCatalog';
import type { FaunaDefinition } from './FaunaDefinition';
import { poseHeightMeters } from './FaunaDefinition';
import type { FaunaInstance } from './FaunaInstance';
import { instanceScale } from './FaunaInstance';
import { FaunaFactory, poseOf } from './FaunaFactory';

/**
 * Owns the canonical set of placed {@link FaunaInstance}s and keeps matching Three.js views in sync,
 * exactly as `FloraWorld` does for plants and `SiteObjectWorld` for buildings: records are the source
 * of truth, views are rebuilt from them, and nothing renderer-owned ever becomes canonical.
 *
 * ## Ground is added, never replaced
 *
 * A creature sits at `ground(x, z) + heightOffsetMeters`. That one rule covers all three attachments
 * a placed instance can have — 0 on the ground, the perch's height on a branch, the record's hover
 * height in the air — and it is why the offset is stored on the instance rather than derived here.
 * The world knows where the ground is; only the placement gesture knew what the designer was aiming
 * at.
 *
 * This is deliberately simpler than `SiteObjectWorld`'s footprint sampling. A building is levelled
 * across its whole footprint and skirted into the slope because a foundation is flat; a rabbit is
 * 400 mm long and stands where it stands. Sampling four corners to average them would be arithmetic
 * in support of nothing visible.
 */

/** Samples terrain height at a world (x, z). Absent means a flat site. */
export type GroundSampler = (x: number, z: number) => number;

export class FaunaWorld {
  private readonly instances = new Map<string, FaunaInstance>();
  private readonly views = new Map<string, THREE.Object3D>();
  private readonly factory = new FaunaFactory();
  private ground: GroundSampler | null = null;

  constructor(
    private readonly catalog: FaunaCatalog,
    private readonly root: THREE.Object3D,
  ) {}

  get count(): number {
    return this.instances.size;
  }

  /**
   * The record behind an instance, or `undefined` when this build's catalog does not have it.
   *
   * Undefined rather than throwing, unlike `FloraWorld.definitionFor`. A project saved with a
   * creature a later build dropped should open with that creature missing and everything else
   * intact — the same tolerance the loader already gives a plant whose species is gone.
   */
  definitionFor(instance: FaunaInstance): FaunaDefinition | undefined {
    return this.catalog.find(instance.faunaDefinitionId);
  }

  /** Point the world at the current terrain. Reseats every view, since ground heights moved. */
  setGroundSampler(sampler: GroundSampler | null): void {
    this.ground = sampler;
    for (const instance of this.instances.values()) this.seat(instance);
  }

  add(instance: FaunaInstance): void {
    if (this.instances.has(instance.id)) {
      throw new Error(`Fauna instance already exists: ${instance.id}`);
    }
    this.instances.set(instance.id, instance);
    this.buildView(instance);
  }

  remove(id: string): FaunaInstance | undefined {
    const instance = this.instances.get(id);
    if (!instance) return undefined;
    this.instances.delete(id);
    this.disposeView(id);
    return instance;
  }

  /**
   * Replace an instance with an updated copy. Only a change that alters the *shape* rebuilds
   * geometry; a move, a turn, or a lock reseats the existing view.
   */
  update(next: FaunaInstance, options: { rebuild?: boolean } = {}): void {
    const previous = this.instances.get(next.id);
    if (!previous) throw new Error(`Cannot update unknown fauna instance: ${next.id}`);
    this.instances.set(next.id, next);

    const needsRebuild =
      options.rebuild ??
      (previous.poseId !== next.poseId ||
        previous.attachment !== next.attachment ||
        previous.faunaDefinitionId !== next.faunaDefinitionId);

    if (needsRebuild || !this.views.has(next.id)) {
      this.disposeView(next.id);
      this.buildView(next);
    } else {
      this.seat(next);
    }
  }

  has(id: string): boolean {
    return this.instances.has(id);
  }

  get(id: string): FaunaInstance | undefined {
    return this.instances.get(id);
  }

  all(): FaunaInstance[] {
    return [...this.instances.values()];
  }

  view(id: string): THREE.Object3D | undefined {
    return this.views.get(id);
  }

  /**
   * Replace the whole set — the "open project" path.
   *
   * Instances naming a record this build does not have are skipped and counted rather than throwing,
   * and the count is returned so the loader can report it the way it already reports skipped plants.
   */
  load(instances: readonly FaunaInstance[]): { placed: number; skipped: number } {
    this.clear();
    let placed = 0;
    let skipped = 0;
    for (const instance of instances) {
      if (this.instances.has(instance.id)) continue;
      if (!this.catalog.has(instance.faunaDefinitionId)) {
        skipped += 1;
        continue;
      }
      this.add(instance);
      placed += 1;
    }
    return { placed, skipped };
  }

  clear(): void {
    for (const id of [...this.instances.keys()]) {
      this.instances.delete(id);
      this.disposeView(id);
    }
  }

  /** Highest point any placed creature reaches, for scene bounds. */
  maxHeight(): number {
    let max = 0;
    for (const instance of this.instances.values()) {
      if (instance.hidden) continue;
      const definition = this.definitionFor(instance);
      if (!definition) continue;
      const height = poseHeightMeters(definition, poseOf(definition, instance));
      max = Math.max(max, instance.heightOffsetMeters + height * instanceScale(instance));
    }
    return max;
  }

  dispose(): void {
    for (const id of [...this.views.keys()]) this.disposeView(id);
    this.factory.dispose();
  }

  private buildView(instance: FaunaInstance): void {
    const definition = this.definitionFor(instance);
    // A record this build does not carry produces no view and no error. The instance stays in the
    // document so saving it again does not quietly delete someone's work.
    if (!definition) return;
    const view = this.factory.build(definition, instance);
    this.views.set(instance.id, view);
    this.root.add(view);
    this.seat(instance);
  }

  /** Put a view where its instance says, on top of the ground under it. */
  private seat(instance: FaunaInstance): void {
    const view = this.views.get(instance.id);
    const definition = this.definitionFor(instance);
    if (!view || !definition) return;

    this.factory.applyTransform(view, definition, instance);
    const base = this.ground?.(instance.position.x, instance.position.z) ?? 0;
    // `sinkMeters` settles a creature into grass rather than perching it on the blades — the same
    // correction the prop catalog applies, and the reason a bench does not float on a lawn.
    view.position.y =
      base + instance.position.y + instance.heightOffsetMeters - definition.rendering.sinkMeters;
  }

  private disposeView(id: string): void {
    const view = this.views.get(id);
    if (!view) return;
    this.root.remove(view);
    this.factory.disposeView(view);
    this.views.delete(id);
  }
}
